import React, { useEffect, useRef, useState } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import ReactDOM from 'react-dom';
import { MoreVertical, Edit2, Trash2, MessageSquare, Share2, Check } from 'lucide-react';
import { WorkItem } from '../../types';

interface ItemMenuProps {
  item: WorkItem;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onComment: () => void;
}

export const ItemMenu: React.FC<ItemMenuProps> = ({
  item,
  isOpen,
  onToggle,
  onClose,
  onEdit,
  onDelete,
  onComment}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showCopied, setShowCopied] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      // Calculate menu position when opened
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 4,
          left: rect.right - 192 // 192px is the width of the menu (w-48 = 12rem = 192px)
        });
      }
      
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  return (
    <div style={styles.table.actionMenuWrapper}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        style={styles.table.actionButton}
        title="Item Actions"
        type="button"
      >
        <MoreVertical size={16} style={styles.table.actionIcon} />
      </button>
      
      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{
            ...styles.table.menuContainer,
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
              onClose();
            }}
            style={styles.table.menuButton}
          >
            <Edit2 size={16} style={styles.table.menuButtonIcon} />
            Edit item
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onComment();
              onClose();
            }}
            style={styles.table.menuButton}
          >
            <MessageSquare size={16} style={styles.table.menuButtonIcon} />
            Add comment
          </button>

          <button
            onClick={async (e) => {
              e.stopPropagation();
              try {
                // Copy link to clipboard
                await navigator.clipboard.writeText(item.link);
                setShowCopied(true);
                // Show feedback for 2 seconds
                setTimeout(() => {
                  setShowCopied(false);
                  onClose();
                }, 2000);
              } catch (err) {
                console.error('Failed to copy link:', err);
                // Fallback method for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = item.link;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                  document.execCommand('copy');
                  setShowCopied(true);
                  setTimeout(() => {
                    setShowCopied(false);
                    onClose();
                  }, 2000);
                } catch (err2) {
                  console.error('Fallback copy failed:', err2);
                }
                document.body.removeChild(textArea);
              }
            }}
            style={{
              ...styles.table.menuButton,
              ...(showCopied ? { backgroundColor: '#10b981', color: '#ffffff' } : {})
            }}
          >
            {showCopied ? (
              <>
                <Check size={16} style={{ color: '#ffffff' }} />
                Link copied!
              </>
            ) : (
              <>
                <Share2 size={16} style={styles.table.menuButtonIcon} />
                Share link
              </>
            )}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              onClose();
            }}
            style={styles.table.menuButton}
          >
            <Trash2 size={16} style={{
              color: '#ef4444'
            }} />
            Delete item
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};